import { t } from '../../i18n/i18n';
import type {
  FillParams,
  Hoop,
  MachineSettings,
  RunningParams,
  SatinParams,
  StitchParams,
  StitchType,
} from './types';
import type { Issue } from './issues';

export interface Limit {
  min: number;
  max: number;
  step: number;
  unit: 'mm' | '°' | '' | 'spm' | 's';
  label: string;
}

/** Validated ranges for every numeric stitch parameter. UI controls and validation both use these. */
export const LIMITS = {
  running: {
    stitchLength: { min: 0.5, max: 8, step: 0.1, unit: 'mm', label: 'Stitch length' },
    repeats: { min: 1, max: 9, step: 1, unit: '', label: 'Passes' },
  },
  fill: {
    angle: { min: -180, max: 180, step: 1, unit: '°', label: 'Stitch angle' },
    spacing: { min: 0.2, max: 3, step: 0.05, unit: 'mm', label: 'Row spacing (density)' },
    stitchLength: { min: 1, max: 8, step: 0.1, unit: 'mm', label: 'Stitch length' },
    minStitchLength: { min: 0.2, max: 2, step: 0.1, unit: 'mm', label: 'Minimum stitch length' },
    stagger: { min: 1, max: 12, step: 1, unit: '', label: 'Stagger (rows)' },
    pullCompensation: { min: 0, max: 2, step: 0.05, unit: 'mm', label: 'Pull compensation' },
    overlap: { min: 0, max: 2, step: 0.05, unit: 'mm', label: 'Overlap' },
  },
  fillUnderlay: {
    inset: { min: 0, max: 3, step: 0.1, unit: 'mm', label: 'Inset' },
    spacing: { min: 0.8, max: 6, step: 0.1, unit: 'mm', label: 'Row spacing' },
    stitchLength: { min: 1, max: 8, step: 0.1, unit: 'mm', label: 'Stitch length' },
  },
  satin: {
    spacing: { min: 0.2, max: 2, step: 0.05, unit: 'mm', label: 'Density (spacing, same side)' },
    pullCompensation: { min: 0, max: 2, step: 0.05, unit: 'mm', label: 'Pull compensation' },
    lineWidth: { min: 0.8, max: 12, step: 0.1, unit: 'mm', label: 'Width (line)' },
    maxWidth: { min: 3, max: 12, step: 0.5, unit: 'mm', label: 'Max width before split' },
  },
  satinUnderlay: {
    inset: { min: 0, max: 2, step: 0.05, unit: 'mm', label: 'Inset' },
    zigzagSpacing: { min: 0.8, max: 5, step: 0.1, unit: 'mm', label: 'Zigzag spacing (same side)' },
    stitchLength: { min: 1, max: 6, step: 0.1, unit: 'mm', label: 'Stitch length' },
  },
  machine: {
    trimThreshold: { min: 0.5, max: 50, step: 0.5, unit: 'mm', label: 'Trim jumps longer than' },
    connectStitchMax: { min: 0, max: 3, step: 0.1, unit: 'mm', label: 'Sew connections shorter than' },
    maxStitchLength: { min: 3, max: 12.1, step: 0.1, unit: 'mm', label: 'Max stitch length' },
    speedSpm: { min: 100, max: 1500, step: 10, unit: 'spm', label: 'Speed' },
    colorChangeSeconds: { min: 0, max: 300, step: 1, unit: 's', label: 'Time per colour change' },
    trimSeconds: { min: 0, max: 30, step: 0.5, unit: 's', label: 'Time per trim' },
  },
} as const satisfies Record<string, Record<string, Limit>>;

/** Hard upper bound for satin stitch width; machines cannot sew longer stitches reliably. */
export const SATIN_HARD_MAX_WIDTH = 12;

export const defaultRunning = (): RunningParams => ({ type: 'running', stitchLength: 2.5, repeats: 1 });

export const defaultFill = (): FillParams => ({
  type: 'fill',
  angle: 45,
  spacing: 0.4,
  stitchLength: 3.5,
  minStitchLength: 0.6,
  stagger: 4,
  pullCompensation: 0.2,
  overlap: 0,
  underlay: { edgeRun: true, tatami: true, inset: 0.6, spacing: 2.2, stitchLength: 3 },
});

export const defaultSatin = (): SatinParams => ({
  type: 'satin',
  spacing: 0.4,
  pullCompensation: 0.3,
  lineWidth: 3,
  maxWidth: 8,
  splitWide: true,
  shortStitches: true,
  underlay: { centerRun: true, edgeRun: false, zigzag: false, inset: 0.4, zigzagSpacing: 2, stitchLength: 2.5 },
});

export function defaultParams(type: StitchType): StitchParams {
  switch (type) {
    case 'running':
      return defaultRunning();
    case 'fill':
      return defaultFill();
    case 'satin':
      return defaultSatin();
  }
}

export const HOOPS: Hoop[] = [
  { id: '100x100', name: '100 × 100 mm (4×4")', widthMm: 100, heightMm: 100 },
  { id: '130x180', name: '130 × 180 mm (5×7")', widthMm: 130, heightMm: 180 },
  { id: '140x200', name: '140 × 200 mm', widthMm: 140, heightMm: 200 },
  { id: '160x260', name: '160 × 260 mm (6×10")', widthMm: 160, heightMm: 260 },
  { id: '200x200', name: '200 × 200 mm (8×8")', widthMm: 200, heightMm: 200 },
  { id: '240x150', name: '240 × 150 mm', widthMm: 240, heightMm: 150 },
  { id: '200x300', name: '200 × 300 mm (8×12")', widthMm: 200, heightMm: 300 },
  { id: '360x200', name: '360 × 200 mm', widthMm: 360, heightMm: 200 },
  { id: '360x350', name: '360 × 350 mm (commercial)', widthMm: 360, heightMm: 350 },
];

export const defaultMachine = (): MachineSettings => ({
  machineId: null,
  hoop: HOOPS[1],
  hoopCenter: null,
  trimThreshold: 3,
  connectStitchMax: 1,
  maxStitchLength: 7,
  speedSpm: 650,
  colorChangeSeconds: 30,
  trimSeconds: 3,
});

export function clamp(v: number, l: Limit): number {
  if (!Number.isFinite(v)) return l.min;
  return Math.min(l.max, Math.max(l.min, v));
}

function checkRange(issues: Issue[], v: number, l: Limit, objectId?: string): void {
  if (!Number.isFinite(v) || v < l.min || v > l.max) {
    issues.push({
      severity: 'error',
      code: 'param-range',
      objectId,
      message: t('{label} must be between {min} and {max}{unit} (is {value}).', {
        label: t(l.label),
        min: l.min,
        max: l.max,
        unit: l.unit ? ' ' + l.unit : '',
        value: Number.isFinite(v) ? v : t('invalid'),
      }),
    });
  }
}

/** Validates all numeric parameters against LIMITS. */
export function validateParams(p: StitchParams, objectId?: string): Issue[] {
  const issues: Issue[] = [];
  switch (p.type) {
    case 'running':
      checkRange(issues, p.stitchLength, LIMITS.running.stitchLength, objectId);
      checkRange(issues, p.repeats, LIMITS.running.repeats, objectId);
      if (!Number.isInteger(p.repeats)) {
        issues.push({ severity: 'error', code: 'param-range', objectId, message: t('Passes must be a whole number.') });
      }
      break;
    case 'fill':
      for (const k of Object.keys(LIMITS.fill) as (keyof typeof LIMITS.fill)[]) checkRange(issues, p[k], LIMITS.fill[k], objectId);
      for (const k of Object.keys(LIMITS.fillUnderlay) as (keyof typeof LIMITS.fillUnderlay)[])
        checkRange(issues, p.underlay[k], LIMITS.fillUnderlay[k], objectId);
      if (p.minStitchLength > p.stitchLength) {
        issues.push({ severity: 'error', code: 'param-range', objectId, message: t('Minimum stitch length cannot be larger than the stitch length.') });
      }
      break;
    case 'satin':
      for (const k of Object.keys(LIMITS.satin) as (keyof typeof LIMITS.satin)[]) checkRange(issues, p[k], LIMITS.satin[k], objectId);
      for (const k of Object.keys(LIMITS.satinUnderlay) as (keyof typeof LIMITS.satinUnderlay)[])
        checkRange(issues, p.underlay[k], LIMITS.satinUnderlay[k], objectId);
      break;
  }
  return issues;
}

export function validateMachine(m: MachineSettings): Issue[] {
  const issues: Issue[] = [];
  for (const k of Object.keys(LIMITS.machine) as (keyof typeof LIMITS.machine)[]) checkRange(issues, m[k], LIMITS.machine[k]);
  if (!(m.hoop.widthMm > 0 && m.hoop.heightMm > 0)) {
    issues.push({ severity: 'error', code: 'hoop', message: t('The hoop must have a positive width and height.') });
  }
  return issues;
}
